/**
 * AgentTypeMenu - Menu for selecting agent type when adding an agent
 *
 * Displays options for "Foundry agent" and "Workflow agent".
 */

import {
  Menu,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  AgentVariantIcon,
  useAgentBadgeClass,
  type AgentVariant,
} from '../helpers/agentVariant';

const useStyles = makeStyles({
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  menuItemTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  menuItemDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
  },
});

// Re-exported for back-compat with existing imports.
export type AgentType = AgentVariant;

export interface AgentTypeMenuProps {
  /** Whether the menu is open */
  open: boolean;
  /** Reference to the anchor element for positioning */
  anchorEl: HTMLElement | null;
  /** Called when an agent type is selected */
  onSelect: (type: AgentType) => void;
  /** Called when the menu should close */
  onClose: () => void;
}

/**
 * AgentTypeMenu - Dropdown for selecting agent type (Foundry tools or native tools)
 */
export const AgentTypeMenu = ({
  open,
  anchorEl,
  onSelect,
  onClose,
}: AgentTypeMenuProps) => {
  const styles = useStyles();
  const badgeClass = useAgentBadgeClass();

  const handleSelect = (type: AgentType) => {
    onSelect(type);
    onClose();
  };

  const renderIcon = (variant: AgentVariant) => (
    <div className={mergeClasses(styles.iconContainer, badgeClass(variant))}>
      <AgentVariantIcon variant={variant} />
    </div>
  );

  return (
    <Menu
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
      positioning={{ target: anchorEl, position: 'below', align: 'start' }}
    >
      <MenuPopover>
        <MenuList>
          <MenuItem
            icon={renderIcon('foundry')}
            onClick={() => handleSelect('foundry')}
          >
            <div className={styles.menuItem}>
              <Text className={styles.menuItemTitle}>Foundry agent</Text>
              <Text className={styles.menuItemDescription}>
                Configure an agent from Azure AI Foundry
              </Text>
            </div>
          </MenuItem>
          <MenuItem
            icon={renderIcon('native')}
            onClick={() => handleSelect('native')}
          >
            <div className={styles.menuItem}>
              <Text className={styles.menuItemTitle}>Workflow agent</Text>
              <Text className={styles.menuItemDescription}>
                Use built-in connectors and tools
              </Text>
            </div>
          </MenuItem>
          <MenuItem
            icon={renderIcon('coding')}
            onClick={() => handleSelect('coding')}
          >
            <div className={styles.menuItem}>
              <Text className={styles.menuItemTitle}>Coding agent</Text>
              <Text className={styles.menuItemDescription}>
                Runs in a sandboxed harness with repos and file outputs
              </Text>
            </div>
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

AgentTypeMenu.displayName = 'AgentTypeMenu';
